import {useBrandStore} from '../../shared/brand/brandStore';
import {BrandAppDownload} from '../../shared/brand/BrandAppDownload';
/**
 * The redesigned hall — route `lobby.next`, reachable at `#/hall-next`.
 *
 * Runs **beside** the current lobby rather than replacing it: `LobbyPage` is
 * untouched, so the live hall keeps shipping while this one is tuned. The room
 * picker, the presence chips and the results trail are imported from
 * `LobbyPage` rather than copied — the two halls must not drift apart while
 * both exist, and when this one wins, the shared parts move into their own
 * module and `LobbyPage` goes away.
 *
 * Layout (2026-08-20 review):
 *   trust line ......... what the product promises, one line, permanent
 *   JACKPOTS BUILDING .. hero = highest live Milhar tier, then the rest + the free raffle
 *   LIVE RIGHT NOW ..... the three room games, one row each
 *   social tail ........ ranking + friends, merged into one row
 *
 * The match ladder inside the hero is the one piece of teaching that stays on
 * screen for everyone. It is a **diagram, not a sentence**: the same idea
 * written as prose tested as fine print that the eye skips. Every figure in it
 * comes from `config.multipliers` — see `payoutLadder`, which exists precisely
 * because a hardcoded payout starts lying the moment someone tunes RTP.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowRight,
  ChevronRight,
  Crown,
  History,
  LayoutGrid,
  ShieldCheck,
  UserRound,
  ChevronDown,
} from 'lucide-react';
import type { CSSProperties, MouseEvent as ReactMouseEvent, TouchEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameCode, Room } from '../../entities/room/types';
import { userVipLevel, userWalletBrl } from '../../entities/user/types';
import { useAppStore } from '../../app/appStore';
import type { MessageKey } from '../../shared/i18n/messages';
import { useAuthStore } from '../../features/auth/authStore';
import { PublicProfileSheet, profileSeed } from '../../features/profile/PublicProfileSheet';
import { fetchMilharStatus, type MilharStatus } from '../../features/milhar/api';
import { MatchLadderRows } from '../../features/milhar/MatchLadderRows';
import { liveMilharTiers, upcomingMilharTiers, type MilharTier, type MilharTierId } from '../../shared/milhar/tiers';
import { payoutLadder } from '../../shared/milhar/rulesWindow';
import { injectionDelta, livePoolTakeHome } from '../../shared/milhar/poolRise';
import { countdownView } from '../../shared/milhar/countdownView';
import { formatRaffleCountdown } from '../../shared/lib/raffleCountdown';
import { useServerClock, useServerNowSeconds } from '../../shared/lib/serverClock';
import { fetchGamePresence, fetchHallBets, fetchRooms } from '../../features/lobby/api';
import { fetchArchiveRounds, type ArchiveRound } from '../../features/archive/api';
import { fetchRaffleData } from '../../features/wallet/api';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { DEFAULT_LEADERBOARD_PERIOD, fetchLeaderboard } from '../../features/leaderboard/api';
import {
  agoParts, drainDelayMs, feedWatermark, hasFeed, injectionFromAmount, pushFeed, seedFeed, tickerOrders, unseenOrders,
} from '../../shared/milhar/roundFeedView';
import {
  hallOnlineCount, hallOrdersFromMilhar, mergeById, shouldFallBackToMilhar, usableHallOrders,
  type HallOrder, type HallOrderUser,
} from '../../shared/hall/betFeedView';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import { ApiError } from '../../shared/api/httpClient';
import { poolOf } from '../../shared/game/rafflePrize';
import { BrandOwl, BrandWordmark } from '../../shared/brand/BrandOwl';
import { WalletChip } from '../../shared/wallet/WalletChip';
import { findBetOption, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { GameIcon } from '../../shared/game/GameIcon';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency, fmtInt, fmtMoney } from '../../shared/lib/format';
import { readStorage, writeStorage } from '../../shared/lib/storage';
import { GameRoomsInline } from './GameRoomsInline';
import { useCountryStore } from '../../shared/country/countryStore';
import { marketCopyKey } from '../../shared/country/marketCopy';
import {
  LandingIntro, MiniDice, brTodayDate, groupRooms, hhmmBRT, trailChip,
} from './LobbyPage';

const PULL_REFRESH_THRESHOLD = 64;
const LAST_ROOM_KEY = 'sorteia:lastRoom';
// Same key the previous hall used, so a guest who already dismissed the intro
// there is not shown it again after the swap.
const LANDING_SEEN_KEY = 'fd:landingSeen';
type PullState = 'idle' | 'pulling' | 'armed' | 'refreshing';

export function LobbyNextPage() {
  // Which game's rooms are unfolded under its card. Not a history layer on
  // purpose: an open list is a state of the hall, not a screen — back should
  // leave the hall, not "close the list first" (2026-09-09 decision).
  const [selectedGame, setSelectedGame] = useState<GameCode | null>(null);
  const openPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedGame) return;
    const panel = openPanelRef.current;
    if (!panel) return;
    // Minimal scroll: only as far as needed to bring the unfolded rooms into
    // view; the tapped card stays where the finger was.
    //
    // Not on the next frame: the panel animates open from 0px (lobby-next.css),
    // so a scroll measured that early sees a list with no height, decides it is
    // already in view and leaves the rooms under the fold — on an iPhone SE the
    // first tap on Último Dígito appeared to do nothing (2026-09-09 QA). Wait
    // for the height to settle; the timer covers reduced-motion, where no
    // transition runs.
    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === panel && event.propertyName === 'grid-template-rows') reveal();
    };
    panel.addEventListener('transitionend', onTransitionEnd);
    const timer = window.setTimeout(reveal, 320);
    return () => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(timer);
    };
  }, [selectedGame]);
  // Carried over from the hall this replaced. A guest's first visit still gets
  // the full-screen pitch — losing a first-run experience as a side effect of a
  // layout swap is not a decision anyone would have made on purpose.
  const [landingSeen, setLandingSeen] = useState(() => readStorage(
    typeof window === 'undefined' ? undefined : window.localStorage, LANDING_SEEN_KEY) === '1');
  const [pullState, setPullState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const pullStartY = useRef(0);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const logout = useAuthStore((state) => state.logout);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const setRoute = useAppStore((state) => state.setRoute);
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  // 下注流里点某个人 → 公开主页压在大厅上（关注、最近下注），关掉还在大厅。
  // 游客也能开：公开主页接口不要登录，弹层里的「关注」对游客就是去注册的入口
  // （排行榜那条路早就是这么走的）。
  const [profileFor, setProfileFor] = useState<FeedProfileTarget | null>(null);
  useI18nStore((state) => state.language);
  const queryClient = useQueryClient();

  // Same cache keys as the current hall: previewing this screen warms the live
  // one instead of doubling every poll.
  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms, refetchInterval: 60_000 });
  const presenceQuery = useQuery({
    queryKey: ['lobby-game-presence'],
    queryFn: fetchGamePresence,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 8_000,
  });
  const trailQuery = useQuery({
    queryKey: ['lobby-trail'],
    queryFn: () => fetchArchiveRounds(brTodayDate(), 40),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const trailRounds = useMemo(() => {
    return (trailQuery.data?.rounds || [])
      .filter((round) => String(round.status || '').toUpperCase() === 'SETTLED' && round.games)
      .sort((a, b) => b.round_number - a.round_number)
      .slice(0, 8);
  }, [trailQuery.data]);

  const rooms = roomsQuery.data?.rooms || [];
  const vipLevel = userVipLevel(user);
  const groupedRooms = useMemo(() => groupRooms(rooms), [rooms]);
  const roomsReliable = !roomsQuery.isError;

  useEffect(() => {
    if (!roomsReliable) setSelectedGame(null);
  }, [roomsReliable]);

  // Hero = the hardest live tier. That is a fixed hierarchy, not a
  // largest-pool-wins sort: the ladder is designed so the harder draw carries
  // the headline figure (tiers.ts), and a layout that reorders itself as pools
  // move would teach players nothing they could rely on.
  const liveTiers = liveMilharTiers();
  const heroTier: MilharTier | undefined = liveTiers[liveTiers.length - 1];
  const sideTiers = liveTiers.slice(0, -1);
  const soonTier = upcomingMilharTiers()[0];

  function goToAuth(target: 'auth.login' | 'auth.register') {
    if (guestMode) clearGuest();
    logout();
    setRoute(target, { replace: true });
  }

  function enterRoom(room: Room) {
    writeStorage(typeof window === 'undefined' ? undefined : window.localStorage, LAST_ROOM_KEY, room.id);
    useAppStore.getState().setActiveRoomId(room.id);
    useAppStore.getState().setRoute('room');
  }

  function pullStart(event: TouchEvent<HTMLDivElement>) {
    if (pullState === 'refreshing' || event.currentTarget.scrollTop > 0) return;
    pullStartY.current = event.touches[0]?.clientY || 0;
    setPullState('pulling');
  }
  function pullMove(event: TouchEvent<HTMLDivElement>) {
    if (pullState !== 'pulling' && pullState !== 'armed') return;
    if (event.currentTarget.scrollTop > 0) { resetPull(); return; }
    const dy = (event.touches[0]?.clientY || 0) - pullStartY.current;
    if (dy <= 0) { setPullDistance(0); setPullState('pulling'); return; }
    const nextDistance = Math.min(140, dy * 0.55);
    if (nextDistance > 8) event.preventDefault();
    setPullDistance(nextDistance);
    setPullState(nextDistance >= PULL_REFRESH_THRESHOLD ? 'armed' : 'pulling');
  }
  function pullEnd() {
    if (pullState === 'armed') { refreshLobby(); return; }
    if (pullState !== 'refreshing') resetPull();
  }
  function resetPull() {
    setPullState('idle');
    setPullDistance(0);
  }
  async function refreshLobby() {
    setPullState('refreshing');
    setPullDistance(56);
    await Promise.all([
      roomsQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: ['milhar-lobby'] }),
      token ? queryClient.invalidateQueries({ queryKey: ['me', token] }) : Promise.resolve(),
    ]);
    window.setTimeout(resetPull, 180);
  }
  const pullLabel = pullState === 'refreshing' ? t('refreshing') : pullState === 'armed' ? t('releaseToRefresh') : t('pullToRefresh');

  const mousePulling = useRef(false);
  function pullMouseStart(event: ReactMouseEvent<HTMLDivElement>) {
    if (pullState === 'refreshing' || event.currentTarget.scrollTop > 0) return;
    mousePulling.current = true;
    pullStartY.current = event.clientY;
    setPullState('pulling');
  }
  function pullMouseMove(event: ReactMouseEvent<HTMLDivElement>) {
    if (!mousePulling.current) return;
    if (pullState !== 'pulling' && pullState !== 'armed') return;
    if (event.currentTarget.scrollTop > 0) { mousePulling.current = false; resetPull(); return; }
    const dy = event.clientY - pullStartY.current;
    if (dy <= 0) { setPullDistance(0); setPullState('pulling'); return; }
    const nextDistance = Math.min(140, dy * 0.55);
    setPullDistance(nextDistance);
    setPullState(nextDistance >= PULL_REFRESH_THRESHOLD ? 'armed' : 'pulling');
  }
  function pullMouseEnd() {
    if (!mousePulling.current) return;
    mousePulling.current = false;
    pullEnd();
  }

  const totalTables = rooms.length;
  const twinCount = sideTiers.length + 1;

  return (
    <section className="lobby-screen ds-screen hallnext">
      <header className="ds-header shrink-0">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <BrandOwl size={34} />
          <div className="min-w-0 flex items-center">
            <BrandWordmark height={18} />
          </div>
        </div>
        {!guestMode ? (
          <WalletChip onOpen={() => setRoute('wallet')} balance={userWalletBrl(user)} />
        ) : (
          /* Both offers live here rather than in the app-level guest banner:
             one row, and sign-up gets the gold because it is the conversion. */
          <span className="hn-auth">
            <button className="hn-btn is-ghost tap" type="button" onClick={() => goToAuth('auth.login')}>
              {t('login')}
            </button>
            <button className="hn-btn is-gold tap" type="button" onClick={() => goToAuth('auth.register')}>
              {t('guestBannerCTA')}
            </button>
          </span>
        )}
      </header>
      <BrandAppDownload />

      <div
        className="hallnext-scroll no-scrollbar"
        onTouchStart={pullStart}
        onTouchMove={pullMove}
        onTouchEnd={pullEnd}
        onTouchCancel={pullEnd}
        onMouseDown={pullMouseStart}
        onMouseMove={pullMouseMove}
        onMouseUp={pullMouseEnd}
        onMouseLeave={pullMouseEnd}
      >
        <div
          className="ptr-indicator"
          style={{
            opacity: pullState === 'idle' ? 0 : Math.min(1, pullDistance / 40),
            transform: `translateY(${pullDistance}px)`,
          }}
        >
          <div
            className={`ptr-spinner ${pullState === 'refreshing' ? 'spinning' : ''}`}
            style={pullState === 'refreshing' ? undefined : { transform: `rotate(${pullState === 'armed' ? 180 : Math.min(180, pullDistance * 2.8)}deg)` }}
          >
            {pullState === 'refreshing' ? <History size={18} aria-hidden="true" /> : <ArrowDownCircle size={18} aria-hidden="true" />}
          </div>
          <span className="text-[10px] text-ink-3 uppercase tracking-widest">{pullLabel}</span>
        </div>

        <div
          style={{
            transform: `translateY(${pullDistance}px)`,
            transition: pullState === 'idle' || pullState === 'refreshing' ? 'transform 220ms cubic-bezier(.2,.8,.2,1)' : 'none',
          }}
        >
          <ProductCreed onRules={() => setRoute('rules')} />

          <h1 className="sr-only">{t('lobbyChooseGame')}</h1>

          {/* ---------- track 1: jackpots ---------- */}
          <div className="hn-sec">
            <span className="hn-sec-t is-money">{t('hallNextJackpots')}</span>
            {soonTier ? (
              <span className="hn-sec-m">{t(soonTier.titleKey)} <em>{t('hallNextTierSoon')}</em></span>
            ) : null}
          </div>

          {heroTier ? (
            <MilharHero tier={heroTier} onOpen={() => setRoute(milharRouteFor(heroTier.id))} />
          ) : null}

          <div className="hn-twins" style={twinCount === 1 ? { gridTemplateColumns: '1fr' } : undefined}>
            {sideTiers.map((tier) => (
              <MilharTwin key={tier.id} tier={tier} onOpen={() => setRoute(milharRouteFor(tier.id))} />
            ))}
            <RaffleTwin onOpen={() => setRoute('sorteios')} />
          </div>

          {/* ---------- track 2: live rooms ---------- */}
          <div className="hn-sec">
            <span className="hn-sec-t is-live">{t('hallNextLive')}</span>
            {totalTables > 0 ? (
              <span className="hn-sec-m">{t('hallNextTablesDraw').replace('%n%', String(totalTables))}</span>
            ) : null}
          </div>

          {roomsQuery.isLoading ? (
            <div className="hn-state">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-brand-500/30 border-t-brand-500" style={{ animation: 'ptrSpin 0.8s linear infinite' }} />
              <b>{t('loadingRooms')}</b>
            </div>
          ) : null}

          {roomsQuery.isError ? (
            <div className="hn-state">
              <div className="hn-state-ico is-error"><AlertTriangle size={22} aria-hidden="true" /></div>
              <b>{t('roomLoadError')}</b>
              <p>{t('roomLoadErrorBody')}</p>
              <button type="button" onClick={() => roomsQuery.refetch()}>{t('retry')}</button>
            </div>
          ) : null}

          {!roomsQuery.isLoading && !roomsQuery.isError && groupedRooms.length === 0 ? (
            <div className="hn-state">
              <div className="hn-state-ico"><LayoutGrid size={22} aria-hidden="true" /></div>
              <b>{t('noRooms')}</b>
              <p>{t('noRoomsBody')}</p>
              <button type="button" onClick={() => roomsQuery.refetch()}>{t('retry')}</button>
            </div>
          ) : null}

          {groupedRooms.length > 0 ? (
            <div className="hn-live">
              {groupedRooms.map((group) => {
                const open = selectedGame === group.game;
                return (
                  <div key={group.game} className={`hn-game${open ? ' is-open' : ''}`} data-hall-game={group.game}>
                    <GameRow
                      game={group.game}
                      roomCount={group.rooms.length}
                      online={presenceQuery.data?.games[group.game]}
                      trail={trailRounds}
                      expanded={open}
                      onChoose={() => setSelectedGame(open ? null : group.game)}
                    />
                    {/* Always mounted so the height can animate; inert when closed. */}
                    <div className="hn-rooms-panel" aria-hidden={!open} ref={open ? openPanelRef : undefined}>
                      <div className="hn-rooms-panel-inner">
                        {open ? (
                          <GameRoomsInline
                            rooms={group.rooms}
                            vipLevel={vipLevel}
                            walletBrl={userWalletBrl(user)}
                            guestMode={guestMode}
                            onEnter={enterRoom}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* ---------- tail ---------- */}
          <HallFloor tier={heroTier} onOpenProfile={setProfileFor} />

          {/* No "see all draws" link: the hall ends on the compliance line. The
              archive is still one hop away through the rules page, which the
              three-beat strip opens (RulesPage.tsx:356). */}
          {!roomsQuery.isLoading && !roomsQuery.isError && groupedRooms.length > 0 ? (
            <div className="ds-comply"><b>18+</b><span>{t(marketCopyKey('complyLine', countryCode))}</span></div>
          ) : null}
          <div className="h-4" />
        </div>
      </div>
      {profileFor ? (
        <PublicProfileSheet user={profileSeed(profileFor)} onClose={() => setProfileFor(null)} />
      ) : null}
      {guestMode && !landingSeen ? (
        <LandingIntro
          trail={trailRounds}
          onDismiss={(goRules) => {
            setLandingSeen(true);
            writeStorage(typeof window === 'undefined' ? undefined : window.localStorage, LANDING_SEEN_KEY, '1');
            if (goRules) setRoute('rules');
          }}
        />
      ) : null}
    </section>
  );
}

function milharRouteFor(tier: MilharTierId) {
  return tier === '5' ? 'milhar.hora' : 'milhar';
}

/** Shared by the hero and the side cards so both read one cache entry per tier. */
function useMilharTier(tier: MilharTierId) {
  const token = useAuthStore((state) => state.token);
  const countryCode = useCountryStore((state) => state.country.code);
  const query = useQuery({
    queryKey: ['milhar-lobby', tier, token || 'guest', countryCode],
    queryFn: () => fetchMilharStatus(token, tier),
    staleTime: 2_000,
    // The headline is a live figure, so this interval is the ceiling on how
    // often it can move. 5s matches the purchase page — the jackpot is the
    // reason people stop on this screen, and a number that only moves twice a
    // minute does not read as accumulating.
    refetchInterval: 5_000,
  });
  // Every countdown on this screen runs on the server's clock, not the phone's
  // (two phones showed the same round 20s apart). This query is the one thing
  // here that carries a `serverTime` and refreshes often, so it is the source.
  const reportServerTime = useServerClock((state) => state.reportServerTime);
  const serverTime = query.data?.serverTime;
  const receivedAt = query.dataUpdatedAt;
  useEffect(() => { reportServerTime(serverTime, receivedAt); }, [reportServerTime, serverTime, receivedAt]);
  return query;
}

/**
 * The headline jackpot, with the mechanic underneath it.
 *
 * Renders nothing when the tier is off: an unavailable game must show no figure
 * at all rather than a stale or borrowed one.
 */
function MilharHero({ tier, onOpen }: { tier: MilharTier; onOpen: () => void }) {
  const t = useI18nStore((state) => state.t);
  const query = useMilharTier(tier.id);
  const status = query.data;
  const pool = status?.pool;
  const contribPct = status?.config?.jackpotContribPct;
  const retainPct = status?.config?.seedRetainPct;
  const injected = status?.roundFeed?.injected;
  const rise = useInjectionRise(injected, contribPct, retainPct);
  // What this round has put in so far, shown on its own line as well as folded
  // into the headline — the line names where the growth is coming from.
  const roundPool = injectionFromAmount(injected ?? 0, contribPct, retainPct);
  // The figure a winner would actually take right now. `pool.takeHome` leaves
  // this round's own stake out, which the settlement maths puts in — see
  // livePoolTakeHome. So this is not unsettled money added on top; it is the
  // understatement corrected, and it moves with every bet.
  const takeHome = useCountUp(livePoolTakeHome(pool?.takeHome, injected, contribPct, retainPct));
  const people = hasFeed(status?.roundFeed) ? status.roundFeed.people : 0;

  if (query.isSuccess && !status?.enabled) return null;

  const price = Number(status?.config?.ticketPrice ?? NaN);
  const currency = pool?.currency || 'BRL';
  // Rounds mean nothing to a player; days do. Floored, and rounds × cadence is
  // a lower bound on real elapsed time, so the figure never overstates.
  const dryDays = pool ? Math.floor((pool.roundsAccumulating * tier.cadenceMinutes) / 1440) : 0;

  return (
    /* A div, not one big button: the card has two actions now — open the game,
       and open the paytable — and a button cannot contain a button. */
    <div className="hn-hero" data-milhar-tier={tier.id} data-hallnext-hero="true">
      <span className="hn-hero-top">
        <span className="hn-hero-name">{t(tier.titleKey)}</span>
        <TierCountdown status={status} />
      </span>
      {/* Cadence and digit count say what kind of game this is, so they belong
          to the name, not to the prize. Below the figure they were mixed in with
          how long the pot has run dry — a fact about the prize — and the two
          kinds of information had to be told apart by reading. */}
      <span className="hn-hero-sub">{t(tier.cadenceKey)}</span>

      {pool ? (
        <span className="hn-hero-amtwrap">
          {/* alwaysCents: the figure changes on its own now, and integer vs
              cents differ by ~42px in mono — it would twitch sideways every
              time it crossed a whole real. */}
          <FitAmount className={`hn-hero-amt${rise ? ' is-lift' : ''}`} text={fmtCurrency(takeHome, currency, undefined, true)} />
          {/* The pool really is filling up; the number should show it moving
              rather than silently swapping value between polls. */}
          {rise ? <span className="hn-hero-plus" key={rise.at}>+{fmtCurrency(rise.delta, currency, undefined, true)}</span> : null}
        </span>
      ) : (
        <span className="hn-hero-amt-sk" aria-hidden="true" />
      )}

      {/* Two rows, split by whether the fact moves.
          Green carries what is happening right now — this round's injection and
          the people in it. Grey carries what is always true — cadence, digits,
          how long the pot has run dry. Before this they were interleaved in one
          run-on line of four facts at the same weight, which is what made the
          card read as clutter: nothing was grouped, so the eye had no landing
          point. Same number of facts, two groups instead of none. */}
      {roundPool > 0 || people > 0 ? (
        <span className="hn-hero-live">
          {roundPool > 0 ? (
            <b className={rise ? 'is-lift' : undefined}>
              {t('hallNextRoundAdded').replace('%v%', fmtCurrency(roundPool, currency, undefined, true))}
            </b>
          ) : null}
          {roundPool > 0 && people > 0 ? <span className="hn-hero-sep">·</span> : null}
          {/* No presence dot here. The row is already green, which is this
              screen's "happening now" channel — a dot beside the separator just
              put two marks in the same 6px. */}
          {people > 0 ? <span>{t('hallNextPlaying').replace('%n%', String(people))}</span> : null}
        </span>
      ) : null}

      {pool && dryDays >= 1 ? (
        <span className="hn-hero-meta">
          {dryDays === 1 ? t('hallNextDryDay') : t('hallNextDryDays').replace('%n%', String(dryDays))}
        </span>
      ) : pool && pool.roundsAccumulating > 0 ? (
        <span className="hn-hero-meta">
          {t('milharPoolRounds').replace('%n%', String(pool.roundsAccumulating))}
        </span>
      ) : null}

      <MatchLadder status={status} />

      <button type="button" className="hn-hero-go tap" onClick={onOpen}>
        {Number.isFinite(price)
          ? t('hallNextPick').replace('%v%', fmtCurrency(price, currency))
          : t('hallNextPickShort')}
      </button>
    </div>
  );
}

function TierCountdown({ status, compact }: { status: MilharStatus | undefined; compact?: boolean }) {
  const nowSec = useServerNowSeconds();
  const t = useI18nStore((state) => state.t);
  const round = status?.round;
  if (!round?.endsAt) return null;
  const view = countdownView(round.endsAt, nowSec, round.phase === 'WAITING' ? 'waiting' : 'open');
  // One clock format across the hall, and it is the one with units.
  //
  // `countdownView` still decides tone and the WAITING case, but its mm:ss is
  // not what gets rendered: the raffle card carries a literal draw time in its
  // subtitle, and a bare "44:55" beside "13:00" reads as a clock — the mistake
  // formatRaffleCountdown was written to prevent. Units are unambiguous
  // everywhere, so the hall speaks one language instead of two.
  //
  // No "draw in" label either: `endsAt` is when *betting closes*, not when the
  // draw happens (visual spec §6.2 calls it the 封盘 countdown), so the label
  // was both inconsistent with the twin cards and slightly wrong.
  const label = view.clock ? formatRaffleCountdown(round.endsAt, nowSec) : t('milharFeedLive');
  return (
    <span className={compact ? 'hn-tw-cd' : 'hn-cd'} data-tone={view.tone}>
      {label}
    </span>
  );
}

/**
 * The payout ladder as a three-column diagram: one digit, a middle step, all of
 * them. Every figure comes from `config.multipliers` via `payoutLadder` — the
 * jackpot column shows the live pool, because that tier has no fixed multiplier.
 *
 * The digits are the **last real draw** (`recentResults[0]`), labelled as such.
 * With no result to show yet, the cells render blank rather than carrying an
 * invented number.
 */
/**
 * The paytable, collapsed.
 *
 * Open, this is a five-row reference table — and a reference table is consulted
 * once and never again, while a hero is read every visit. Leaving it expanded
 * charged the most valuable strip on the screen a permanent 120px for a
 * one-time need, and pushed everything else below the fold.
 *
 * The collapsed line is not a label: it carries the differentiator itself —
 * one digit already pays — so the fact a player most needs stays visible even
 * when the table is shut.
 */
function MatchLadder({ status }: { status: MilharStatus | undefined }) {
  const t = useI18nStore((state) => state.t);
  const [open, setOpen] = useState(false);
  const config = status?.config;
  const pool = status?.pool;
  if (!config || !pool) return null;

  const digits = Number(config.digits);
  const price = Number(config.ticketPrice);
  if (!Number.isFinite(digits) || digits <= 1) return null;

  const rows = payoutLadder(config.multipliers, digits, price);
  const shown = rows.filter((row) => row.matched === digits || row.payout != null);
  if (shown.length < 2) return null;

  const first = shown[0];
  const drawn = String(status?.recentResults?.[0]?.result || '');

  return (
    <div className={`hn-lad${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="hn-lad-toggle tap"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="hn-lad-teaser">
          {first.payout != null
            ? t('hallNextPayTeaser').replace('%v%', fmtCurrency(Number(first.payout), pool.currency))
            : t('milharPayoutHint')}
        </span>
        <ChevronRight size={14} className="hn-lad-caret" aria-hidden="true" />
      </button>

      {open ? (
        <MatchLadderRows
          digits={digits}
          rows={shown}
          drawn={drawn}
          jackpot={Number(pool.takeHome)}
          currency={pool.currency}
        />
      ) : null}
    </div>
  );
}

/**
 * Rolls a figure to its new value instead of snapping.
 *
 * <p>Every value it passes through lies between two real readings and it always
 * lands exactly on the real one — it interpolates, it never invents a number
 * beyond what the server said. On a screen whose headline is "don't believe us,
 * do the math", a jackpot that drifts past the true figure would be the one
 * lie the whole product cannot afford.
 */
/**
 * A money figure that shrinks to fit its card instead of breaking in two.
 *
 * <p>ARS runs about three digits longer than BRL for the same prize, and the
 * hero is 41px mono: `$ 19.829.100,00` overflowed, and the `overflow-wrap:
 * anywhere` that was there to stop it widening the card broke the number
 * *mid-figure* — an Android screenshot showed the final `0` alone on a second
 * line. A jackpot split across two lines is worse than a small one.
 *
 * <p>The size is computed in CSS from the character count and the container's own
 * width (`cqw`); nothing is measured here. It has to be that way: `useCountUp`
 * re-renders this on every animation frame while the pool ticks up, and a
 * measure-then-resize pass per frame is a forced synchronous reflow 60 times a
 * second on the screen's headline element. The type is monospace, so the
 * character count *is* the width — no measurement is needed to know it.
 *
 * <p>Browsers without container query units keep the previous behaviour (fixed
 * size, wrap rather than clip) via the `@supports` guard in the stylesheet, so
 * this degrades to exactly what shipped before rather than to an overflow.
 */
function FitAmount({ className, text }: { className: string; text: string }) {
  return (
    <span className={className} style={{ '--hn-amt-len': text.length } as CSSProperties}>
      {text}
    </span>
  );
}

function useCountUp(target: number, durationMs = 900) {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(0);
  useEffect(() => {
    if (!Number.isFinite(target)) return undefined;
    const from = fromRef.current;
    if (from === target) return undefined;
    // A first real value must not roll up from zero — that is a slot-machine
    // flourish, not a change that happened.
    if (!Number.isFinite(from) || from <= 0) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // A hidden tab does not run requestAnimationFrame, so an animation started
    // there never finishes and the figure freezes at whatever frame it reached.
    // On a jackpot that means coming back to the app and reading a stale number.
    if (reduced || document.hidden) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }
    const started = Date.now();
    const step = () => {
      const p = Math.min(1, (Date.now() - started) / durationMs);
      const eased = 1 - (1 - p) * (1 - p) * (1 - p);
      setShown(p >= 1 ? target : from + (target - from) * eased);
      if (p < 1) rafRef.current = window.requestAnimationFrame(step);
      else fromRef.current = target;
    };
    rafRef.current = window.requestAnimationFrame(step);
    // Belt and braces for the same reason: if the frames stop coming for any
    // reason, the true value still lands. A money figure may never be left
    // showing an interpolation.
    const guard = window.setTimeout(() => {
      fromRef.current = target;
      setShown(target);
    }, durationMs + 250);
    return () => {
      window.cancelAnimationFrame(rafRef.current);
      window.clearTimeout(guard);
    };
  }, [target, durationMs]);
  return shown;
}

/**
 * The live rise, driven by this round's bets.
 *
 * Measured on 2026-08-21 (tier 4): across one round `roundFeed.injected` went
 * 14 → 18 → 22 while `pool.takeHome` never moved, then at the round boundary
 * takeHome stepped 5336.00 → 5340.40 — exactly 22 × 25% × (1 − 20%). So the
 * headline figure settles **once per round**; watching it for an injection
 * animation means one flash an hour on the hourly tier, which is why nothing
 * appeared to move. The number that moves with every bet is `injected`, and
 * `injectionDelta` puts it through the same arithmetic the server settles with.
 */
function useInjectionRise(
  injected: string | undefined,
  contribPct: string | undefined,
  retainPct: string | undefined,
) {
  const [rise, setRise] = useState<{ delta: number; at: number } | null>(null);
  const prev = useRef<string | undefined>(undefined);
  useEffect(() => {
    const before = prev.current;
    prev.current = injected;
    const delta = injectionDelta(before, injected, contribPct, retainPct);
    if (delta !== null) setRise({ delta, at: Date.now() });
  }, [injected, contribPct, retainPct]);
  useEffect(() => {
    if (!rise) return undefined;
    const id = window.setTimeout(() => setRise(null), 1400);
    return () => window.clearTimeout(id);
  }, [rise]);
  return rise;
}

/** A lower Milhar tier, beside the free raffle. Same silence rule as the hero. */
function MilharTwin({ tier, onOpen }: { tier: MilharTier; onOpen: () => void }) {
  const t = useI18nStore((state) => state.t);
  const query = useMilharTier(tier.id);
  const status = query.data;
  const contribPct = status?.config?.jackpotContribPct;
  const retainPct = status?.config?.seedRetainPct;
  const injected = status?.roundFeed?.injected;
  const rise = useInjectionRise(injected, contribPct, retainPct);
  const pool = status?.pool;
  const twinTakeHome = useCountUp(livePoolTakeHome(pool?.takeHome, injected, contribPct, retainPct));
  if (query.isSuccess && !status?.enabled) return null;
  const roundPool = injectionFromAmount(injected ?? 0, contribPct, retainPct);

  return (
    <button type="button" className="hn-tw is-money tap" onClick={onOpen} data-milhar-tier={tier.id}>
      <span className="hn-tw-nm">
        <span>{t(tier.titleKey)}</span>
        <TierCountdown status={status} compact />
      </span>
      <span className="hn-tw-mt">{t(tier.cadenceKey)}</span>
      {pool
        ? (
          <span className="hn-tw-amtwrap">
            <FitAmount className={`hn-tw-amt${rise ? ' is-lift' : ''}`} text={fmtCurrency(twinTakeHome, pool.currency, undefined, true)} />
          </span>
        )
        : <span className="hn-tw-amt-sk" aria-hidden="true" />}
      {/* Only the injection, not the people count as well: the card is ~154px
          of usable width and the hero's full line needs about 200px, so both
          facts would wrap into two ragged lines. This is the one that answers
          "is the pot growing". */}
      {roundPool > 0 && pool ? (
        <span className={`hn-tw-live${rise ? ' is-lift' : ''}`}>
          {t('hallNextRoundAdded').replace('%v%', fmtCurrency(roundPool, pool.currency, undefined, true))}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The free raffle. Shares the ['raffle', token] cache with the Draws page, so
 * it is usually warm. Guests get the same live figure: `/api/raffle` serves
 * anonymous callers scoped by the selected-country header.
 */
function RaffleTwin({ onOpen }: { onOpen: () => void }) {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const viewer = raffleViewerMode(token, guestMode);
  const query = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token || ''),
    enabled: raffleReadsEnabled(viewer),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const nowSec = useServerNowSeconds();
  const event = query.data?.event || null;
  const status = String(event?.status || '').toLowerCase();
  const open = status === 'registering';
  const held = ['locked', 'full', 'settling'].includes(status);
  const pool = poolOf(event?.prize_brackets);
  const time = event?.scheduled_at ? hhmmBRT(event.scheduled_at) : '';
  const live = (open || held) && pool > 0;
  // Units, not mm:ss — a bare "10:24" beside the literal draw time 11:00 reads
  // as a clock, which is the mistake formatRaffleCountdown exists to prevent.
  // Counts down for a locked/full draw too: the clock runs to the draw itself,
  // which is still the thing the player is waiting for once entry has closed.
  const left = live && event?.scheduled_at
    ? formatRaffleCountdown(event.scheduled_at, nowSec)
    : '';

  return (
    <button type="button" className="hn-tw is-free tap" onClick={onOpen} data-hallnext-raffle="true">
      <span className="hn-tw-nm">
        <span>{t('sorteiosTitle')}</span>
        {left ? <span className="hn-tw-cd is-free">{left}</span> : null}
      </span>
      {/* Live: descriptor under the name and the figure last, matching the Milhar
          card so the two amounts sit on one line across the pair.
          No live event (none scheduled, or the read failed): the offer itself is the headline. */}
      {/* FREE rides on the meta line rather than the title, and it is the whole
          claim -- the line used to read "13:00 · no cost", which overstates it:
          entry costs no money but it does cost a ticket (see hallNextRaffleEntry
          right below, "enter with tickets"). "Free" is the honest version of
          that promise, and saying it twice on one card was never needed. */}
      {live ? (
        <>
          <span className="hn-tw-mt">
            {time ? <span>{time}</span> : null}
            <span className="hn-tw-badge">{t('hallNextRaffleFree')}</span>
          </span>
          <span className="hn-tw-amtwrap">
            <FitAmount className="hn-tw-amt" text={fmtMoney(pool)} />
          </span>
        </>
      ) : (
        <>
          <span className="hn-tw-word">{t('hallNextRaffleGuest')}</span>
          <span className="hn-tw-mt">
            <span>{t('hallNextRaffleEntry')}</span>
            <span className="hn-tw-badge">{t('hallNextRaffleFree')}</span>
          </span>
        </>
      )}
    </button>
  );
}

const GAME_PITCH: Record<GameCode, MessageKey> = {
  lastdigit: 'lobbyGameLastdigit',
  sicbo: 'lobbyGameSicbo',
  roulette: 'lobbyGameRoulette',
};

/**
 * How many past results fit on one row, per game.
 *
 * Not one number for all three: a Sic Bo result is three dice (~58px), a
 * LastDigit result is one 19px cell. Six dice groups would need ~350px and push
 * the row into a second line, so each game gets the count its own result shape
 * can actually carry.
 */
const GAME_TRAIL_LIMIT: Record<GameCode, number> = {
  lastdigit: 6,
  roulette: 6,
  sicbo: 3,
};

/**
 * One live room, as a room and not as a menu entry.
 *
 * Carries the game's one-line pitch. This product has no market comparable, so
 * a bare title teaches nothing — a player who has never seen "Sic Bo" cannot
 * infer it from the word. The copy is the lobby's existing description, already
 * translated; the row just puts it where the decision is made.
 *
 * The live tag and the pulsing presence dot are what make the row read as a
 * table with people at it rather than a link to a category.
 */
function GameRow({ game, roomCount, online, trail, expanded, onChoose }: {
  game: GameCode;
  roomCount: number;
  online: number | undefined;
  trail: ArchiveRound[];
  expanded: boolean;
  onChoose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const showOnline=useBrandStore(state=>state.showOnlineCount);
  const chips = trail
    .map((round) => ({ round, chip: trailChip(game, round) }))
    .filter((item) => item.chip)
    .slice(0, GAME_TRAIL_LIMIT[game]);
  const tables = (roomCount === 1 ? t('hallNextRoomsOne') : t('hallNextTables')).replace('%n%', String(roomCount));

  return (
    <button
      type="button"
      className={`hn-row tap${expanded ? ' is-open' : ''}`}
      data-game={game}
      onClick={onChoose}
      aria-expanded={expanded}
      aria-label={`${t('lobbyChooseRoomAction')} · ${t(gameNameKey(game))}`}
    >
      <span className="hn-row-ico" aria-hidden="true"><GameIcon game={game} /></span>

      <span className="hn-row-body">
        <span className="hn-row-head">
          <span className="hn-row-nm">{t(gameNameKey(game))}</span>
          {showOnline && online !== undefined && online > 0 ? (
            <span className="hn-row-live">
              <span className="hn-row-live-dot" aria-hidden="true" />
              {t('hallNextLiveTag')}
            </span>
          ) : null}
        </span>

        <span className="hn-row-pitch">{t(GAME_PITCH[game])}</span>

        {/* Bottom line: who is in there on the left, what came out on the right.
            The results close the row because that is where the eye already is
            after reading the pitch — on the title line they competed with the
            game's own name for the same span.

            No chevron: the whole row is the button, and an arrow on every row of
            a three-row card was one mark per row earning nothing. The press
            feedback from `.tap` and the card's own grouping carry the
            affordance. */}
        <span className="hn-row-foot">
          <span className="hn-row-meta">
            {!showOnline || online === undefined
              ? tables
              : `${t('lobbyOnlineNow').replace('%n%', fmtInt(online))} · ${tables}`}
          </span>
          <span className="hn-row-trail" aria-hidden="true">
            {chips.map(({ round, chip }) => (
              chip?.dice ? (
                <span key={round.round_number} className="ds-dice-result">
                  {chip.dice.map((value, index) => <MiniDice key={`${round.round_number}-${index}`} value={value} />)}
                </span>
              ) : (
                <span key={round.round_number} className={`ds-pk ${chip?.cls || ''}`}>{chip?.label}</span>
              )
            ))}
          </span>
        </span>
      </span>
      <ChevronDown size={16} className="hn-row-cv" aria-hidden="true" />
    </button>
  );
}

/**
 * The hall floor: who is playing right now, then who is ahead.
 *
 * Two objects, deliberately not merged. The feed is **ambience** — nobody taps
 * a ticker, it only has to be alive. The ranking is a **destination** — people
 * go to it on purpose. The single merged row this replaced made the ranking
 * look untappable and left the feed no room to exist at all.
 *
 * The feed shows **participation, not wins**. A stream of "X just won R$ 5.000"
 * is survivorship bias staged as news: a recognised dark pattern, the first
 * thing responsible-gaming scrutiny looks for, and a direct contradiction of a
 * product whose pitch is "check it yourself" rather than "everyone here wins".
 * Real names, real picks, real stakes, real timestamps — that is the honest
 * version of the same energy, and it is the data the API already publishes.
 *
 * Publishing identity, amount and pick is the standing PM decision recorded in
 * `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §1.1, along with the obligation that
 * players are told before they buy. That notice lives on the purchase page.
 * Since 2026-09-14 guests see the same names and avatars as members (PM
 * decision, `doc/HALL_BET_FEED_CONTRACT.md` §1): the block exists to make the
 * hall feel alive and to pull guests into registering, and a column of grey
 * silhouettes labelled `player_xxx` did the opposite.
 *
 * The count in the header is the real one: distinct clients heartbeating
 * site-wide (`/api/rooms/presence` `total`), not how many rows happen to be
 * on screen — that fallback is capped at the window size, so the header used
 * to read "10 playing right now" all day. See `hallOnlineCount`.
 *
 * Nothing to show → the whole block does not render (§3.1: an empty social
 * module hurts the atmosphere more than no module at all).
 */
function HallFloor({ tier, onOpenProfile }: {
  tier: MilharTier | undefined;
  onOpenProfile: (user: FeedProfileTarget) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const setRoute = useAppStore((state) => state.setRoute);

  const feedQuery = useMilharTier(tier?.id ?? '5');
  const milharFeed = tier ? feedQuery.data?.roundFeed : undefined;
  const currency = feedQuery.data?.pool?.currency || 'BRL';

  // The cross-game stream. `retry: false` so a missing endpoint fails once and
  // the fallback takes over immediately instead of after three back-offs.
  const hallQuery = useQuery({
    // The token is part of the key: what the endpoint returns depends on it
    // (blurred vs named), so a login must not be served the guest cache.
    queryKey: ['hall-bets', token || 'guest'],
    queryFn: () => fetchHallBets(token, 20),
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
    retry: false,
  });
  const hallStatus = hallQuery.error instanceof ApiError ? hallQuery.error.status : undefined;
  const fellBack = shouldFallBackToMilhar(hallStatus);
  // Milhar-only until the endpoint ships — the block must not disappear while
  // the backend catches up (contract §6).
  const orders = fellBack || hallQuery.isError
    ? hallOrdersFromMilhar(milharFeed, tier?.id === '5' ? 'milhar-hora' : 'milhar', currency, feedQuery.data?.round?.number ?? 0)
    : mergeById(
      usableHallOrders(hallQuery.data),
      hallOrdersFromMilhar(milharFeed, tier?.id === '5' ? 'milhar-hora' : 'milhar', currency, feedQuery.data?.round?.number ?? 0),
    );
  const milharPeople = hasFeed(milharFeed) ? milharFeed.people : 0;

  // Same key as the game rows' presence query above, so this is one poll, not
  // two. `total` is the site-wide distinct count; see `hallOnlineCount` for
  // what happens while a backend without it is still serving.
  const presenceQuery = useQuery({
    queryKey: ['lobby-game-presence'],
    queryFn: fetchGamePresence,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 8_000,
  });
  const onlineTotal = presenceQuery.data?.total;

  const board = useQuery({
    queryKey: ['leaderboard', token || 'guest', DEFAULT_LEADERBOARD_PERIOD, 'all', 'all', 'profit', 'profit', false, 5],
    queryFn: () => fetchLeaderboard({ token: token || undefined, period: DEFAULT_LEADERBOARD_PERIOD, game: 'all', room: 'all' }),
    staleTime: 60_000,
  });
  const top = (board.data?.items || [])
    .filter((item) => item.ranked !== false && Number(item.profit || 0) > 0)
    .slice(0, 3);

  const openRanking = () => setRoute('leaderboard');
  const showFeed = orders.length > 0;
  if (!showFeed && top.length === 0) return null;

  return (
    <section className="hn-floor">
      {showFeed ? (
        <LiveFeed orders={orders} onlineTotal={onlineTotal} milharPeople={milharPeople} onRanking={openRanking}
          onOpenProfile={onOpenProfile} />
      ) : null}

      {/* Only when there is no feed to hang it on: an empty round must not take
          the ranking's entry point down with it. */}
      {!showFeed && top.length > 0 ? (
        <button
          type="button"
          className="hn-link tap"
          onClick={openRanking}
          data-hallnext-ranking="true"
        >
          <Crown size={14} className="hn-link-ico is-gold" aria-hidden="true" />
          <span className="hn-link-tx">{t('lobbyRankingTitle')}</span>
          <span className="hn-link-note">{t('hallNextRankingWindow')}</span>
          <ChevronRight size={15} className="hn-link-arw" aria-hidden="true" />
        </button>
      ) : null}

    </section>
  );
}

/**
 * The live order ticker, compact.
 *
 * All the rules that are easy to break silently — play order, de-duplication,
 * the watermark, how fast to drain a backlog — stay in `roundFeedView`, shared
 * with the purchase page's own feed. Only the window size differs — deep enough
 * that the block reads as a busy hall rather than a two-line notice, and a
 * deeper window also keeps it moving during a quiet stretch: the shallower it
 * is, the sooner it has nothing left to play. The endpoint returns 20, so ten
 * visible rows still leave a buffer to drain from.
 */
const LOBBY_FEED_WINDOW = 10;

type FeedProfileTarget = { userId: number; username: string; avatarUrl?: string };

function LiveFeed({ orders, onlineTotal, milharPeople, onRanking, onOpenProfile }: {
  orders: HallOrder[];
  onlineTotal: number | undefined;
  milharPeople: number;
  onRanking: () => void;
  onOpenProfile: (user: FeedProfileTarget) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [visible, setVisible] = useState<HallOrder[]>([]);
  const seenRef = useRef<Set<number>>(new Set());
  const queueRef = useRef<HallOrder[]>([]);
  const markRef = useRef(0);

  const ticker = useMemo(() => tickerOrders(orders), [orders]);

  useEffect(() => {
    if (!ticker.length || seenRef.current.size) return;
    const seeded = seedFeed(ticker, LOBBY_FEED_WINDOW);
    seeded.forEach((order) => seenRef.current.add(order.id));
    markRef.current = feedWatermark(seeded);
    setVisible(seeded);
  }, [ticker]);

  useEffect(() => {
    if (!ticker.length) return;
    queueRef.current = unseenOrders(ticker, seenRef.current, markRef.current);
  }, [ticker]);

  // A self-scheduling chain, not a fixed interval: the gap has to follow the
  // backlog (drainDelayMs), and an interval's period is fixed when registered.
  useEffect(() => {
    if (!ticker.length) return undefined;
    let timer = 0;
    const tick = () => {
      const next = queueRef.current.shift();
      if (next) {
        seenRef.current.add(next.id);
        setVisible((prev) => pushFeed(prev, next, LOBBY_FEED_WINDOW));
      }
      timer = window.setTimeout(tick, drainDelayMs(queueRef.current.length));
    };
    timer = window.setTimeout(tick, drainDelayMs(queueRef.current.length));
    return () => window.clearTimeout(timer);
  }, [ticker.length]);

  return (
    <div className="hn-feed">
      <button
        type="button"
        className="hn-feed-head tap"
        onClick={onRanking}
        aria-label={`${t('hallNextFloor')} · ${t('lobbyRankingTitle')}`}
        data-hallnext-ranking="true"
      >
        <span className="hn-feed-count">
          <span className="hn-feed-dot" aria-hidden="true" />
          {t('hallNextPlayingNow').replace('%n%', fmtInt(hallOnlineCount(onlineTotal, milharPeople, visible.length)))}
        </span>
        <span className="hn-feed-rank">
          <Crown size={13} aria-hidden="true" />
          {t('lobbyRankingTitle')}
          <ChevronRight size={14} aria-hidden="true" />
        </span>
      </button>

      {/* Doubled so the loop has no seam. Decorative — the count above says it. */}
      <div className="hn-feed-strip" aria-hidden="true">
        <div className="hn-feed-track">
          {orders.concat(orders).map((order, index) => (
            <FeedAvatar key={`${order.id}-${index}`} user={order.user} />
          ))}
        </div>
      </div>

      <div className="hn-feed-rows">
        {visible.map((order, index) => (
          <FeedLine key={order.id} order={order} stale={index > 0} onOpenProfile={onOpenProfile} />
        ))}
      </div>
    </div>
  );
}

function FeedAvatar({ user }: { user: HallOrderUser }) {
  const url = String(user.avatarThumbUrl || '')
    || (Number(user.avatarDefaultId ?? 0) ? defaultAvatarUrl(Number(user.avatarDefaultId)) : '');
  if (url) return <img className="hn-feed-av" src={url} alt="" loading="lazy" />;
  // The server no longer anonymises rows (contract §1, 2026-09-14), but a
  // backend from before that change still can. Such a row gets a silhouette,
  // not an initial: every blurred name starts "player_", so initials turn the
  // strip into a column of identical Ps that reads as a rendering fault.
  if (user.userId === null || user.userId === undefined) {
    return (
      <span className="hn-feed-av is-anon" aria-hidden="true">
        <UserRound size={11} />
      </span>
    );
  }
  return <span className="hn-feed-av is-letter">{(user.username || '?').trim().charAt(0).toUpperCase()}</span>;
}

/**
 * One bet, whatever game it came from.
 *
 * The row used to be Milhar-shaped: a five-cell number chip and an amount that
 * was always R$ 5, because Milhar tickets are a fixed price. Mixed in with room
 * bets neither holds — a roulette pick is "vermelho", a Sic Bo pick is "grande",
 * and stakes run from R$ 2 to R$ 1000, which is the first time that column
 * carries information at all.
 *
 * The pick's words and colour both come from `betOptions` — the same table the
 * bet sheet and the results strips read, so the hall never invents a second
 * vocabulary for the same thing.
 */
function FeedLine({ order, stale, onOpenProfile }: {
  order: HallOrder;
  stale: boolean;
  onOpenProfile: (user: FeedProfileTarget) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const ago = agoParts(order.placedAt, Math.floor(Date.now() / 1000));
  const agoText = ago.unit === 'now'
    ? t('milharFeedNow')
    : t(ago.unit === 's' ? 'milharFeedSecs' : 'milharFeedMins').replace('%n%', String(ago.value));
  const milhar = order.game === 'milhar';
  const option = milhar ? null : findBetOption(order.game as GameCode, { tab: order.tab as never, pick: order.pick });
  // Milhar's pick is the number itself. Unknown games fall through to the raw
  // value rather than being dropped — a build that predates a new game must
  // still show the bet honestly (contract §3.1).
  const label = milhar ? order.pick : pickLabel(order.game, order.tab, order.pick, t);

  // A row from a backend that still anonymises (userId null) has nobody to
  // open, so it stays a plain line — the silhouette avatar already says so.
  const userId = order.user.userId;
  const canOpenProfile = userId !== null && userId !== undefined && !!order.user.username;
  const inner = (
    <>
      <FeedAvatar user={order.user} />
      <span className="hn-feed-name">{order.user.username}</span>
      <span className="hn-feed-game" aria-hidden="true"><GameIcon game={order.game as GameCode} /></span>
      <span className={`hn-feed-pick${option?.tone ? ` is-${option.tone}` : ''}`}>{label}</span>
      <span className="hn-feed-amt">{fmtCurrency(Number(order.amount), order.currency)}</span>
      <span className="hn-feed-ago">{agoText}</span>
    </>
  );
  const rowClass = `hn-feed-row${stale ? ' is-stale' : ' is-fresh'}`;
  if (!canOpenProfile) return <div className={rowClass}>{inner}</div>;
  const avatarUrl = String(order.user.avatarThumbUrl || '')
    || (Number(order.user.avatarDefaultId ?? 0) ? defaultAvatarUrl(Number(order.user.avatarDefaultId)) : '');
  return (
    <button
      type="button"
      className={`${rowClass} hn-feed-row-btn tap`}
      aria-label={t('raffleOpenProfile').replace('%username%', order.user.username)}
      onClick={() => onOpenProfile({ userId: Number(userId), username: order.user.username, avatarUrl: avatarUrl || undefined })}
    >
      {inner}
    </button>
  );
}

/**
 * The creed. Brand book §5.2 — the line it calls the anchor of the whole case.
 *
 * It has only ever lived in ad creative until now; an anchor line that never
 * enters the product is not an anchor. This is the most-viewed slot Sorteia
 * has, and the line has to do a job no hook can: **it sits directly above the
 * jackpot figure**, and whatever stands here is read together with it. The
 * brand's own anti-pillars forbid a get-rich-quick frame, yet the middle of
 * this screen is a rolling R$ 66.983 — this sentence is what reframes that
 * number as the prize in a fair game rather than as a promise being made.
 *
 * Typography inverts the weights on purpose: the refusal is set back, the
 * promise carries the weight, so what stays with the reader is "fair" and not
 * "no guarantee". The link is Zé Coruja's own line (§12.2) rather than a
 * generic label — an invitation, in the brand's voice.
 */
function ProductCreed({ onRules }: { onRules: () => void }) {
  const t = useI18nStore((state) => state.t);
  return (
    <button className="hn-creed tap" type="button" onClick={onRules}>
      {/* Shield + arrow, the same shape the current lobby's trust manifesto uses.
          A guarantee reads as one because it is badged like one — and the arrow
          alone says "there is more here" without spending a line on a label. */}
      <span className="hn-creed-mark" aria-hidden="true"><ShieldCheck size={18} /></span>
      <span className="hn-creed-copy">
        <span className="hn-creed-1">{t('hallNextCreed1')}</span>
        <b className="hn-creed-2">{t('hallNextCreed2')}</b>
      </span>
      <span className="hn-creed-link">
        {/* The arrow carries no words, so the destination is named for screen
            readers instead. */}
        <span className="sr-only">{t('hallNextCheck')}</span>
        <ArrowRight size={16} aria-hidden="true" />
      </span>
    </button>
  );
}

